import { useState, useEffect } from "react";
import api from "../services/api";
import OutreachRadar from "./OutreachRadar";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

async function adminFetch(endpoint, options = {}) {
  const token = await api.getAccessToken();
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...options.headers },
  });
  if (!res.ok) throw new Error("Request failed");
  return res.json();
}

const CONTENT_FIELDS = [
  { key: "about_headline", label: "Headline", rows: 2 },
  { key: "about_mission", label: "Mission paragraph", rows: 3 },
  { key: "about_why_p1", label: "Why we built this — paragraph 1", rows: 4 },
  { key: "about_why_p2", label: "Why we built this — paragraph 2", rows: 4 },
  { key: "about_jacksonville", label: "Jacksonville section", rows: 4 },
  { key: "about_cta", label: "CTA text", rows: 1 },
];

function ContentEditor() {
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/content`)
      .then((r) => r.json())
      .then(setContent)
      .catch(console.error);
  }, []);

  const save = async (key) => {
    setSaving(key);
    try {
      await adminFetch(`/api/admin/content/${key}`, {
        method: "PATCH",
        body: JSON.stringify({ value: content[key] }),
      });
      setSaved(key);
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      alert("Save failed");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: "#888", marginBottom: 24 }}>Changes go live on the About page immediately after saving.</p>
      {CONTENT_FIELDS.map(({ key, label, rows }) => (
        <div key={key} style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1a1a2e", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
          <textarea
            value={content[key] || ""}
            onChange={(e) => setContent((prev) => ({ ...prev, [key]: e.target.value }))}
            rows={rows}
            style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #e0ddd4", borderRadius: 8, fontSize: 14, fontFamily: "Georgia, serif", lineHeight: 1.6, resize: "vertical", boxSizing: "border-box", outline: "none" }}
          />
          <button
            onClick={() => save(key)}
            disabled={saving === key}
            style={{ marginTop: 6, padding: "7px 18px", background: saved === key ? "#16a34a" : "#E8622A", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            {saving === key ? "Saving..." : saved === key ? "✓ Saved" : "Save"}
          </button>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard({ onBack, onSignOut }) {
  const [tab, setTab] = useState("reviews");
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    Promise.all([
      adminFetch("/api/admin/reviews"),
      adminFetch("/api/admin/stats"),
    ]).then(([reviewData, statsData]) => {
      setReviews(reviewData.reviews || []);
      setStats(statsData.stats);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const moderateReview = async (reviewId, approve) => {
    setActionLoading(reviewId);
    try {
      await adminFetch(`/api/admin/reviews/${reviewId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_approved: approve }),
      });
      setReviews((prev) => prev.map((r) => r.id === reviewId ? { ...r, is_approved: approve } : r));
    } catch (e) {
      alert("Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const totalReviews = reviews.length;
  const approvedCount = reviews.filter((r) => r.is_approved).length;
  const pendingCount = reviews.filter((r) => !r.is_approved).length;
  const flaggedCount = reviews.filter((r) => r.is_flagged).length;

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f0" }}>
      {/* Header */}
      <header style={{ background: "#1a1a2e", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 16 }}>← Back</button>
          <span style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>
            Door<span style={{ color: "#E8622A" }}>Report</span>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginLeft: 10, fontFamily: "sans-serif", fontWeight: 400 }}>Admin</span>
          </span>
        </div>
        <button onClick={onSignOut} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 13 }}>Sign Out</button>
      </header>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 20px" }}>
        {/* Stats row */}
        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 28 }}>
            {[
              { label: "Total Reviews", value: totalReviews },
              { label: "Approved", value: approvedCount },
              { label: "Pending", value: pendingCount },
              { label: "Flagged", value: flaggedCount },
              { label: "Pending Flags", value: stats.pending_flags },
              { label: "Banned Users", value: stats.banned_users },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: "#fff", borderRadius: 10, padding: "16px 20px", border: "1px solid #e0ddd4" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#1a1a2e", fontFamily: "Georgia, serif" }}>{value ?? "—"}</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: "2px solid #e0ddd4", marginBottom: 24, overflowX: "auto" }}>
          {[
            { key: "reviews", label: "All Reviews" },
            { key: "content", label: "About Page" },
            { key: "outreach", label: "Outreach Radar" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "10px 20px", border: "none", background: "transparent", cursor: "pointer",
              fontSize: 14, fontWeight: 600, color: tab === t.key ? "#E8622A" : "#888",
              borderBottom: `2px solid ${tab === t.key ? "#E8622A" : "transparent"}`,
              marginBottom: -2, whiteSpace: "nowrap",
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Reviews tab */}
        {tab === "reviews" && (
          <div>
            {loading ? (
              <p style={{ color: "#888", textAlign: "center", padding: 40 }}>Loading reviews...</p>
            ) : reviews.length === 0 ? (
              <p style={{ color: "#888", textAlign: "center", padding: 40 }}>No reviews yet.</p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", marginBottom: 12, border: `1px solid ${r.is_flagged ? "#fca5a5" : "#e0ddd4"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: r.is_approved ? "#dcfce7" : "#fef3c7", color: r.is_approved ? "#166534" : "#92400e" }}>
                          {r.is_approved ? "Approved" : "Pending"}
                        </span>
                        {r.is_flagged && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "#fee2e2", color: "#991b1b" }}>Flagged</span>}
                        <span style={{ fontSize: 11, color: "#888" }}>{"★".repeat(r.overall_rating)} {r.overall_rating}/5</span>
                      </div>
                      <p style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 15, margin: "0 0 2px", color: "#1a1a2e" }}>{r.title}</p>
                      <p style={{ fontSize: 12, color: "#E8622A", margin: "0 0 8px", fontWeight: 600 }}>
                        {r.address_line1}, {r.city}, {r.state} · {r.author_email}
                      </p>
                      <p style={{ fontSize: 13, color: "#444", margin: 0, lineHeight: 1.6 }}>{r.review_text}</p>
                      <p style={{ fontSize: 11, color: "#aaa", margin: "8px 0 0" }}>{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      {!r.is_approved ? (
                        <button
                          onClick={() => moderateReview(r.id, true)}
                          disabled={actionLoading === r.id}
                          style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        >
                          Approve
                        </button>
                      ) : (
                        <button
                          onClick={() => moderateReview(r.id, false)}
                          disabled={actionLoading === r.id}
                          style={{ padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Content tab */}
        {tab === "content" && <ContentEditor />}

        {/* Outreach tab */}
        {tab === "outreach" && <OutreachRadar />}
      </div>
    </div>
  );
}
